import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LayoutDashboard, Users, GraduationCap, Plug, Settings as SettingsIcon, LogOut, GaugeCircle, Gauge, Radio, Sparkles, Megaphone, Tags, Trophy, Send, ShieldAlert, ClipboardCheck } from "lucide-react";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";

const nav = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, id: "dashboard" },
  { to: "/leads", label: "Lead", icon: Users, id: "leads" },
  { to: "/inoltri", label: "Inoltri", icon: Send, id: "forwards" },
  { to: "/inoltri-auto", label: "Inoltri Auto (Test)", icon: ShieldAlert, id: "autofwd" },
  { to: "/vinti", label: "Lead Vinti", icon: Trophy, id: "won" },
  { to: "/report-gestione", label: "Report Gestione", icon: ClipboardCheck, id: "handling" },
  { to: "/pagelle", label: "Pagelle", icon: GraduationCap, id: "pagelle" },
  { to: "/analytics/sources", label: "Canali & Storico", icon: Radio, id: "sources" },
  { to: "/analytics/speed", label: "Analisi Velocità", icon: Gauge, id: "analytics" },
  { to: "/campagne", label: "Campagne", icon: Megaphone, id: "campaigns" },
  { to: "/qualita", label: "Qualità Lead", icon: Tags, id: "quality" },
  { to: "/ricerca-ia", label: "Ricerca IA", icon: Sparkles, id: "ai-search" },
  { to: "/courses", label: "Corsi", icon: GraduationCap, id: "courses" },
  { to: "/integrations", label: "Integrazioni", icon: Plug, id: "integrations" },
  { to: "/settings", label: "Impostazioni", icon: SettingsIcon, id: "settings" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const current = nav.find((n) => location.pathname.startsWith(n.to));

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Sidebar */}
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-[260px] flex-col border-r border-border bg-card md:flex">
        <div className="flex h-16 items-center gap-2 border-b border-border px-6">
          <div className="flex h-9 w-9 items-center justify-center rounded-md bg-primary text-primary-foreground">
            <GaugeCircle className="h-5 w-5" />
          </div>
          <div className="leading-tight">
            <p className="font-display text-sm font-bold tracking-tight">ITALIACORSI</p>
            <p className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground">Sales Control</p>
          </div>
        </div>
        <nav className="flex-1 space-y-1 p-3">
          {nav.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`sidebar-${item.id}-link`}
                className={({ isActive }) =>
                  `flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors ${
                    isActive
                      ? "bg-accent text-accent-foreground"
                      : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                  }`
                }
              >
                <Icon className="h-[18px] w-[18px]" />
                <span className="flex-1">{item.label}</span>
              </NavLink>
            );
          })}
        </nav>
        <div className="border-t border-border p-3">
          <div className="flex items-center gap-3 rounded-md px-2 py-2">
            <Avatar className="h-9 w-9">
              <AvatarImage src={user?.picture} alt={user?.name} />
              <AvatarFallback>{(user?.name || user?.email || "A")[0].toUpperCase()}</AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium">{user?.name || "Amministratore"}</p>
              <p className="truncate text-xs text-muted-foreground">{user?.email}</p>
            </div>
            <Button
              variant="ghost"
              size="icon"
              data-testid="logout-button"
              aria-label="Esci"
              onClick={logout}
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="md:pl-[260px]">
        <header className="sticky top-0 z-20 flex h-16 items-center justify-between border-b border-border bg-card/80 px-6 backdrop-blur">
          <h1 className="font-display text-xl font-bold tracking-tight" data-testid="page-title">
            {current?.label || "Dashboard"}
          </h1>
          <div className="flex items-center gap-2 md:hidden">
            <Avatar className="h-8 w-8">
              <AvatarImage src={user?.picture} alt={user?.name} />
              <AvatarFallback>{(user?.email || "A")[0].toUpperCase()}</AvatarFallback>
            </Avatar>
            <Button variant="ghost" size="icon" aria-label="Esci" onClick={logout}>
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </header>
        <main className="p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
